import { Fragment, useEffect, useState } from 'react';
import {
  styles,
  collections,
  colorToCss,
  colorToHex,
  parseColor,
  cssFont,
  textStyle,
  SCOPES_BY_TYPE,
  cssVariableName,
  type NodeData,
  type VariableValue,
  type Color,
  type VariableScope,
} from '@vellum/core';
import { app } from '../app.ts';
import { useApp } from '../hooks.ts';
import { Icons } from './icons.tsx';

function centerOfView() {
  return app.screenToPage({
    x: app.viewportSize.width / 2,
    y: app.viewportSize.height / 2,
  });
}

export function AssetsPanel() {
  useApp('doc', () => app.docVersion);
  const doc = app.doc;
  const comps = [...doc.nodes.values()].filter((n) => n.type === 'COMPONENT');
  const fillStyles = styles(doc, 'FILL');
  const textStyles = styles(doc, 'TEXT');
  const effectStyles = styles(doc, 'EFFECT');

  const gridStyles = styles(doc, 'GRID');
  const sel = app.editor.selectedNodes();
  return (
    <div className="layers" style={{ padding: '4px 0 40px' }}>
      <Section title="Components" count={comps.length}>
        {comps.length === 0 && <div className="asset-hint">Select layers and press ⌥⌘K to make a component.</div>}
        {comps.map((c) => (
          <div
            key={c.id}
            className="asset-row"
            title="Click to insert an instance"
            onClick={() => {
              const p = centerOfView();
              app.editor.createInstance(c.id, {
                x: Math.round(p.x - c.width / 2),
                y: Math.round(p.y - c.height / 2),
              });
            }}
          >
            <span className="icon" style={{ color: 'var(--component)' }}>
              {Icons.component}
            </span>
            <span className="name">{c.name}</span>
            <span className="meta">{app.doc.pageOf(c.id)?.name}</span>
          </div>
        ))}
      </Section>

      <Section
        title="Color styles"
        count={fillStyles.length}
        onAdd={() => {
          const n = sel[0];
          const fills = n?.fills.length ? n.fills : [{ type: 'SOLID' as const, color: parseColor('#0d99ff') }];
          const name = prompt('Style name', 'Brand/Primary');
          if (!name) return;
          const id = app.editor.createStyle('FILL', name, {
            fills: structuredClone(fills),
          });
          if (n) app.editor.applyStyle([n.id], id, 'fill');
        }}
      >
        <div className="swatch-grid">
          {fillStyles.map((s) => (
            <button
              key={s.id}
              className="style-swatch"
              title={`${s.name} — click: apply as fill, ⇧click: apply as stroke`}
              onClick={(e) => sel.length && app.editor.applyStyle(app.editor.selection, s.id, e.shiftKey ? 'stroke' : 'fill')}
              onContextMenu={(e) => {
                e.preventDefault();
                editStyleColor(s);
              }}
            >
              <i style={{ background: paintPreview(s) }} />
              <span>{s.name}</span>
            </button>
          ))}
        </div>
      </Section>

      <Section
        title="Text styles"
        count={textStyles.length}
        onAdd={() => {
          const n = sel.find((x) => x.type === 'TEXT');
          const name = prompt('Text style name', 'Heading/H1');
          if (!name) return;
          const base = n ?? {
            fontFamily: 'Inter',
            fontSize: 32,
            fontWeight: 700,
          };
          const id = app.editor.createStyle('TEXT', name, {
            fontFamily: base.fontFamily,
            fontSize: base.fontSize,
            fontWeight: base.fontWeight,
            lineHeight: (base as NodeData).lineHeight,
            letterSpacing: (base as NodeData).letterSpacing,
            italic: (base as NodeData).italic,
          });
          if (n) app.editor.applyStyle([n.id], id, 'text');
        }}
      >
        {textStyles.map((s) => (
          <div key={s.id} className="asset-row" title="Click to apply to selected text" onClick={() => sel.length && app.editor.applyStyle(app.editor.selection, s.id, 'text')}>
            <span className="icon" style={{ font: cssFont({ ...textStyle(s), fontSize: 13 }) }}>
              Ag
            </span>
            <span className="name">{s.name}</span>
            <span className="meta">
              {s.fontSize}/{s.fontWeight}
            </span>
          </div>
        ))}
      </Section>

      <Section
        title="Effect styles"
        count={effectStyles.length}
        onAdd={() => {
          const n = sel[0];
          const name = prompt('Effect style name', 'Shadow/Card');
          if (!name) return;
          const effects = n?.effects.length
            ? n.effects
            : [
                {
                  type: 'DROP_SHADOW' as const,
                  color: parseColor('#00000026'),
                  offset: { x: 0, y: 4 },
                  radius: 12,
                },
              ];
          const id = app.editor.createStyle('EFFECT', name, {
            effects: structuredClone(effects),
          });
          if (n) app.editor.applyStyle([n.id], id, 'effect');
        }}
      >
        {effectStyles.map((s) => (
          <div key={s.id} className="asset-row" onClick={() => sel.length && app.editor.applyStyle(app.editor.selection, s.id, 'effect')}>
            <span className="icon">◩</span>
            <span className="name">{s.name}</span>
          </div>
        ))}
      </Section>

      <Section
        title="Grid styles"
        count={gridStyles.length}
        onAdd={() => {
          const n = sel[0];
          const name = prompt('Grid style name', '12 columns');
          if (!name) return;
          const grids = n?.layoutGrids?.length ? n.layoutGrids : [{ pattern: 'COLUMNS' as const, count: 12, gutter: 24, margin: 80 }];
          const id = app.editor.createStyle('GRID', name, { layoutGrids: structuredClone(grids) });
          if (n && ['FRAME', 'COMPONENT', 'INSTANCE'].includes(n.type)) app.editor.applyStyle([n.id], id, 'grid');
        }}
      >
        {gridStyles.map((s) => (
          <div key={s.id} className="asset-row" title="Click: apply to the selected frames" onClick={() => sel.length && app.editor.applyStyle(app.editor.selection.filter((id) => ['FRAME', 'COMPONENT', 'INSTANCE'].includes(app.doc.get(id)?.type ?? '')), s.id, 'grid')}>
            <span className="icon">▥</span>
            <span className="name">{s.name}</span>
            <span className="meta">{(s.layoutGrids ?? []).map((g) => (g.pattern === 'GRID' ? `${g.size ?? 8}px` : `${g.count ?? 0} ${g.pattern.toLowerCase()}`)).join(' + ')}</span>
          </div>
        ))}
      </Section>

      <Variables />
      <Libraries />
      <LibraryAnalytics />
    </div>
  );
}

interface LibItem {
  id: string;
  name: string;
  type: string;
  page?: string;
}

interface Usage {
  id: string;
  name: string;
  type: string;
  uses: number;
  files: { id: string; name: string; uses: number }[];
  otherFiles: number;
  history: { day: string; inserts: number; detaches: number }[];
}

/** Inserts (up) and detaches (down) per day over the last 30 days. */
function Trend({ history }: { history: Usage['history'] }) {
  if (!history.length) return null;
  const days = Array.from({ length: 30 }, (_, i) => new Date(Date.now() - (29 - i) * 864e5).toISOString().slice(0, 10));
  const max = Math.max(1, ...history.map((h) => Math.max(h.inserts, h.detaches)));
  const ins = history.reduce((s, h) => s + h.inserts, 0),
    det = history.reduce((s, h) => s + h.detaches, 0);
  return (
    <div className="usage-trend" title={`Last 30 days: ${ins} inserted, ${det} detached`}>
      <svg width="120" height="24" viewBox="0 0 120 24">
        {days.map((d, i) => {
          const h = history.find((x) => x.day === d);
          if (!h) return null;
          return (
            <g key={d}>
              <rect x={i * 4} y={12 - (h.inserts / max) * 12} width="3" height={(h.inserts / max) * 12} fill="var(--accent)" />
              <rect x={i * 4} y={12} width="3" height={(h.detaches / max) * 12} fill="var(--danger)" />
            </g>
          );
        })}
      </svg>
      <span>
        +{ins} −{det}
      </span>
    </div>
  );
}

/** How this file's components, styles and variables are used in other files. */
function LibraryAnalytics() {
  const [data, setData] = useState<{ items: Usage[]; files: number; uses: number } | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const load = () =>
    fetch(`/api/files/${app.fileId}/library/analytics`)
      .then((r) => r.json())
      .then(setData)
      .catch(() => setData(null));
  return (
    <Section title="Library usage" count={data?.items.length ?? 0} onAdd={load}>
      {!data && (
        <div className="asset-hint">
          See where this file’s components, styles and variables are used.{' '}
          <button className="btn ghost" onClick={load}>
            Show usage
          </button>
        </div>
      )}
      {data && (
        <div className="asset-hint">
          {data.uses} use{data.uses === 1 ? '' : 's'} in {data.files} file{data.files === 1 ? '' : 's'}
        </div>
      )}
      {data?.items.map((u) => (
        <div key={u.id}>
          <div className="asset-row" onClick={() => setOpen(open === u.id ? null : u.id)}>
            <span className="icon">{u.files.length || u.history.length ? (open === u.id ? '▾' : '▸') : '·'}</span>
            <span className="name">{u.name}</span>
            <span style={{ marginLeft: 'auto', color: u.uses ? 'var(--text)' : 'var(--text-3)' }}>{u.uses}</span>
          </div>
          {open === u.id && (
            <>
              <Trend history={u.history} />
              {u.files.map((f) => (
                <a key={f.id} className="asset-row" style={{ paddingLeft: 36 }} href={`?file=${f.id}`}>
                  <span className="name">{f.name}</span>
                  <span style={{ marginLeft: 'auto', color: 'var(--text-2)' }}>{f.uses}</span>
                </a>
              ))}
              {u.otherFiles > 0 && (
                <div className="asset-row" style={{ paddingLeft: 36, color: 'var(--text-2)' }}>
                  + {u.otherFiles} file{u.otherFiles > 1 ? 's' : ''} you can’t open
                </div>
              )}
            </>
          )}
        </div>
      ))}
    </Section>
  );
}

function Libraries() {
  const [files, setFiles] = useState<{ id: string; name: string }[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [items, setItems] = useState<LibItem[]>([]);
  useEffect(() => {
    fetch('/api/files')
      .then((r) => r.json())
      .then((fs: { id: string; name: string }[]) => setFiles(fs.filter((f) => f.id !== app.fileId)))
      .catch(() => {});
  }, []);
  useEffect(() => {
    if (!open) return;
    fetch(`/api/files/${open}/library`)
      .then((r) => r.json())
      .then((d: { items: LibItem[] }) => setItems(d.items))
      .catch(() => setItems([]));
  }, [open]);
  const use = async (item: LibItem) => {
    const c = centerOfView();
    const r = await fetch(`/api/files/${app.fileId}/library`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        source: open,
        itemId: item.id,
        instance:
          item.type === 'COMPONENT'
            ? {
                x: Math.round(c.x),
                y: Math.round(c.y),
                pageId: app.editor.pageId,
              }
            : undefined,
      }),
    });
    const res = (await r.json()) as { instanceId?: string; error?: string };
    if (!r.ok) return app.toast(res.error ?? 'Could not use library item');
    app.toast(item.type === 'COMPONENT' ? `Inserted ${item.name}` : `Added ${item.name}`);
    if (res.instanceId) setTimeout(() => app.editor.setSelection([res.instanceId!]), 200);
  };
  return (
    <Section
      title="Libraries"
      count={files.length}
      onAdd={async () => {
        const r = await fetch(`/api/files/${app.fileId}/library/update`, {
          method: 'POST',
        });
        const d = (await r.json()) as { updated: number };
        app.toast(d.updated ? `Updated ${d.updated} library item(s)` : 'Library items are up to date');
      }}
    >
      <div className="asset-hint">Use components, styles and variables from your other files. + pulls the latest versions.</div>
      {files.map((f) => (
        <div key={f.id}>
          <div className="asset-row" onClick={() => setOpen(open === f.id ? null : f.id)}>
            <span className="icon">{open === f.id ? '▾' : '▸'}</span>
            <span className="name">{f.name}</span>
          </div>
          {open === f.id &&
            (items.length ? (
              items.map((it) => (
                <div key={it.id} className="asset-row" style={{ paddingLeft: 36 }} title={`Use ${it.name}`} onClick={() => use(it)}>
                  <span
                    className="icon"
                    style={{
                      color: it.type.startsWith('COMPONENT') ? 'var(--component)' : undefined,
                    }}
                  >
                    {it.type.startsWith('COMPONENT') ? Icons.component : it.type === 'STYLE' ? '◈' : '◆'}
                  </span>
                  <span className="name">{it.name}</span>
                  <span className="meta">{it.type === 'VARIABLE_COLLECTION' ? 'variables' : it.type.toLowerCase().replace('_', ' ')}</span>
                </div>
              ))
            ) : (
              <div className="asset-hint" style={{ paddingLeft: 36 }}>
                Nothing published yet.
              </div>
            ))}
        </div>
      ))}
    </Section>
  );
}

function paintPreview(s: NodeData) {
  const f = s.fills[0];
  if (!f) return 'transparent';
  if (f.type === 'SOLID') return colorToCss(f.color, f.opacity ?? 1);
  if ('stops' in f) return `linear-gradient(90deg, ${f.stops.map((st) => colorToCss(st.color)).join(', ')})`;
  return '#ccc';
}

function editStyleColor(s: NodeData) {
  const f = s.fills[0];
  const cur = f?.type === 'SOLID' ? colorToHex(f.color) : '#000000';
  const v = prompt(`New color for "${s.name}"`, cur);
  if (!v) return;
  try {
    app.editor.update(s.id, { fills: [{ type: 'SOLID', color: parseColor(v) }] }, 'Edit style');
  } catch {
    app.toast('Not a color');
  }
}

function Section({ title, count, onAdd, children }: { title: string; count: number; onAdd?: () => void; children: React.ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="panel-section" style={{ borderBottom: '1px solid var(--border)' }}>
      <div className="panel-title">
        <span style={{ cursor: 'pointer' }} onClick={() => setOpen(!open)}>
          {open ? '▾' : '▸'} {title} <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>{count || ''}</span>
        </span>
        {onAdd && (
          <button title={`New ${title.toLowerCase().replace(/s$/, '')}`} onClick={onAdd}>
            {Icons.plus}
          </button>
        )}
      </div>
      {open && children}
    </div>
  );
}

const SCOPE_LABELS: Record<VariableScope, string> = {
  ALL_SCOPES: 'All supported properties',
  ALL_FILLS: 'All fills',
  FRAME_FILL: 'Frame fills',
  SHAPE_FILL: 'Shape fills',
  TEXT_FILL: 'Text fills',
  STROKE_COLOR: 'Strokes',
  EFFECT_COLOR: 'Effects',
  CORNER_RADIUS: 'Corner radius',
  WIDTH_HEIGHT: 'Width and height',
  GAP: 'Gap and padding',
  OPACITY: 'Layer opacity',
  STROKE_FLOAT: 'Stroke weight',
  EFFECT_FLOAT: 'Effect values',
  FONT_SIZE: 'Font size',
  LINE_HEIGHT: 'Line height',
  LETTER_SPACING: 'Letter spacing',
  TEXT_CONTENT: 'Text content',
};

/** Scopes (where the variable may be used) and its names in code. */
function VariableSettings({ v }: { v: NodeData }) {
  const all = !v.scopes?.length || v.scopes.includes('ALL_SCOPES');
  const options = SCOPES_BY_TYPE[v.resolvedType ?? 'COLOR'];
  const setScopes = (scopes: VariableScope[]) => app.editor.update(v.id, { scopes: scopes.length ? scopes : ['ALL_SCOPES'] }, 'Variable scopes');
  const toggle = (s: VariableScope, on: boolean) => {
    const cur = all ? [] : (v.scopes ?? []);
    setScopes(on ? [...cur, s] : cur.filter((x) => x !== s));
  };
  const syntax = (key: 'WEB' | 'ANDROID' | 'iOS', value: string) => {
    const next = { ...v.codeSyntax, [key]: value || undefined };
    app.editor.update(v.id, { codeSyntax: Object.values(next).some(Boolean) ? next : undefined }, 'Code syntax');
  };
  return (
    <div className="var-settings">
      {options.length > 0 && (
        <>
          <div className="var-settings-title">Show in</div>
          <label>
            <input type="checkbox" checked={all} onChange={(e) => setScopes(e.target.checked ? ['ALL_SCOPES'] : [options[0]])} /> {SCOPE_LABELS.ALL_SCOPES}
          </label>
          {!all &&
            options.map((s) => (
              <label key={s}>
                <input type="checkbox" checked={!!v.scopes?.includes(s)} onChange={(e) => toggle(s, e.target.checked)} /> {SCOPE_LABELS[s]}
              </label>
            ))}
        </>
      )}
      <div className="var-settings-title">Code syntax</div>
      {(['WEB', 'ANDROID', 'iOS'] as const).map((k) => (
        <label key={k} className="var-syntax">
          <span>{k === 'WEB' ? 'Web' : k === 'ANDROID' ? 'Android' : 'iOS'}</span>
          <input
            defaultValue={v.codeSyntax?.[k] ?? ''}
            placeholder={k === 'WEB' ? cssVariableName({ name: v.name }) : k === 'ANDROID' ? 'colorPrimary' : 'Color.primary'}
            onBlur={(e) => syntax(k, e.target.value.trim())}
            onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
          />
        </label>
      ))}
    </div>
  );
}

function Variables() {
  const [editing, setEditing] = useState<string | null>(null);
  const doc = app.doc;
  const cols = collections(doc);
  return (
    <Section
      title="Variables"
      count={cols.reduce((s, c) => s + doc.children(c.id).length, 0)}
      onAdd={() => {
        const name = prompt('Collection name', cols.length ? 'Tokens' : 'Theme');
        if (!name) return;
        const modes = (prompt('Modes (comma separated)', 'Light, Dark') ?? 'Default')
          .split(',')
          .map((m) => m.trim())
          .filter(Boolean);
        app.editor.createVariableCollection(name, modes.length ? modes : ['Default']);
      }}
    >
      {cols.length === 0 && <div className="asset-hint">Variables are tokens with modes (e.g. Light / Dark). Bind them to fills, spacing, radii or text, then switch modes per frame.</div>}
      {cols.map((c) => (
        <div key={c.id} className="var-collection">
          <div className="var-head">
            <b>
              {c.name}
              {c.extendsCollection && <span className="var-extends"> extends {doc.get(c.extendsCollection)?.name ?? '?'}</span>}
            </b>
            <span style={{ display: 'flex', gap: 4 }}>
              <button
                className="btn ghost"
                title="A collection that inherits these variables and modes and overrides some values (e.g. a brand theme)"
                onClick={() => {
                  const name = prompt('Name of the extended collection', `${c.name} theme`);
                  if (name) app.editor.createExtendedCollection(c.id, name);
                }}
              >
                Extend
              </button>
              {!c.extendsCollection && (
                <button
                  className="btn ghost"
                  onClick={() => {
                    const name = prompt('Variable name', 'colors/primary');
                    if (!name) return;
                    const type = (prompt('Type: COLOR, FLOAT, STRING or BOOLEAN', 'COLOR') ?? 'COLOR').toUpperCase() as 'COLOR';
                    const init: VariableValue = type === 'COLOR' ? parseColor('#0d99ff') : (type as string) === 'FLOAT' ? 8 : (type as string) === 'BOOLEAN' ? true : 'Text';
                    app.editor.createVariable(c.id, name, type, Object.fromEntries((c.modes ?? []).map((m) => [m.id, init])));
                  }}
                >
                  + Variable
                </button>
              )}
              {!c.extendsCollection && (
                <button
                  className="btn ghost"
                  onClick={() =>
                    (() => {
                      const n = prompt('Mode name', 'Dark');
                      if (n) app.editor.addMode(c.id, n);
                    })()
                  }
                >
                  + Mode
                </button>
              )}
            </span>
          </div>
          <table className="var-table">
            <thead>
              <tr>
                <th>Name</th>
                {c.modes?.map((m) => (
                  <th key={m.id}>{m.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {baseVariables(c).map((v) => (
                <Fragment key={v.id}>
                  <tr>
                    <td
                      className="vname"
                      title={
                        v.resolvedType === 'COLOR'
                          ? 'Click: bind selection fill · ⇧click: stroke'
                          : v.resolvedType === 'FLOAT'
                            ? 'Click: bind selection gap (⇧ radius, ⌥ padding)'
                            : 'Click: bind selection text'
                      }
                      onClick={(e) => {
                        const ids = app.editor.selection;
                        if (!ids.length) return;
                        const prop =
                          v.resolvedType === 'COLOR'
                            ? e.shiftKey
                              ? 'stroke'
                              : 'fill'
                            : v.resolvedType === 'FLOAT'
                              ? e.shiftKey
                                ? 'cornerRadius'
                                : e.altKey
                                  ? 'paddingLeft'
                                  : 'itemSpacing'
                              : v.resolvedType === 'STRING'
                                ? 'characters'
                                : 'visible';
                        try {
                          app.editor.bindVariable(ids, prop, v.id);
                          if (prop === 'paddingLeft') app.editor.transact('Bind padding', () => ['paddingRight', 'paddingTop', 'paddingBottom'].forEach((p) => app.editor.bindVariable(ids, p, v.id)));
                          app.toast(`Bound ${prop} to ${v.name}`);
                        } catch (err) {
                          app.toast((err as Error).message);
                        }
                      }}
                    >
                      {v.name}
                      <button className="var-gear" title="Scopes and code syntax" onClick={(e) => (e.stopPropagation(), setEditing(editing === v.id ? null : v.id))}>
                        ⚙
                      </button>
                    </td>
                    {c.modes?.map((m) => (
                      <td key={m.id}>
                        <VarCell v={v} modeId={m.id} ext={c.extendsCollection ? c : undefined} />
                      </td>
                    ))}
                  </tr>
                  {editing === v.id && (
                    <tr>
                      <td colSpan={(c.modes?.length ?? 1) + 1}>
                        <VariableSettings v={v} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </Section>
  );
}

/** An extended collection shows its base collection's variables. */
function baseVariables(c: NodeData): NodeData[] {
  let base: NodeData | undefined = c;
  for (let i = 0; base?.extendsCollection && i < 8; i++) base = app.doc.get(base.extendsCollection);
  return base ? app.doc.children(base.id).filter((v) => v.type === 'VARIABLE') : [];
}

/** A value cell. In an extended collection it edits an override, and shows inherited values dimmed. */
function VarCell({ v, modeId, ext }: { v: NodeData; modeId: string; ext?: NodeData }) {
  if (ext) {
    const own = ext.variableOverrides?.[v.id]?.[modeId];
    return (
      <span className={own === undefined ? 'var-inherited' : 'var-overridden'}>
        <ValueCell v={v} val={own ?? v.valuesByMode?.[modeId]} set={(next) => app.editor.setVariableOverride(ext.id, v.id, modeId, next)} />
        {own !== undefined && (
          <button className="var-reset" title="Inherit again" onClick={() => app.editor.setVariableOverride(ext.id, v.id, modeId, null)}>
            ↺
          </button>
        )}
      </span>
    );
  }
  return <ValueCell v={v} val={v.valuesByMode?.[modeId]} set={(next) => app.editor.setVariableValue(v.id, modeId, next)} />;
}

function ValueCell({ v, val, set }: { v: NodeData; val: VariableValue | undefined; set: (v: VariableValue) => void }) {
  if (val && typeof val === 'object' && 'alias' in val) return <span className="chip">{`{${app.doc.get(val.alias)?.name}}`}</span>;
  if (v.resolvedType === 'COLOR') {
    const c = (val as Color) ?? { r: 0, g: 0, b: 0, a: 1 };
    return (
      <span className="swatch" style={{ width: 20, height: 20 }}>
        <i style={{ background: colorToCss(c) }} />
        <input
          type="color"
          value={colorToHex(c, false)}
          onChange={(e) =>
            set({
              ...parseColor(e.target.value),
              a: c.a,
            })
          }
        />
      </span>
    );
  }
  if (v.resolvedType === 'BOOLEAN') return <input type="checkbox" checked={!!val} onChange={(e) => set(e.target.checked)} />;
  return (
    <input
      className="var-input"
      defaultValue={String(val ?? '')}
      key={String(val)}
      onBlur={(e) => {
        const raw = e.target.value;
        const next = v.resolvedType === 'FLOAT' ? Number(raw) : raw;
        if (v.resolvedType === 'FLOAT' && !Number.isFinite(next)) return;
        if (next !== val) set(next);
      }}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      }}
    />
  );
}
